import type {
  CalendarClient,
  CalendarEvent,
  CalendarEventDraft,
  Calendar as CalendarModel,
  CalendarParticipant,
  CalendarParticipationStatus,
  RecurrenceScope,
} from '@wtfalch/calendar/calendar';
import {
  Button,
  Callout,
  Checkbox,
  Empty,
  Field,
  Icon,
  Input,
  Menu,
  Modal,
  Panes,
  Pill,
  Select,
  Textarea,
  Toggle,
} from '@wtfalch/design';
import { counter } from '@wtfalch/email/imip';
import type { MailClient } from '@wtfalch/email/mailbox';
import { identities as fetchMailIdentities, send as sendMail } from '@wtfalch/email/mailbox';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useCallSession } from '../calls/CallSession.tsx';
import { CallSurface } from '../calls/CallSurface.tsx';
import { createEventCall, endEventCall } from '../calls/eventCallSetup.ts';
import {
  eventCallRoomId,
  eventCallUrl,
  eventCallWindow,
  parseEventCallUrl,
  parseGuestEntry,
} from '../calls/eventCalls.ts';
import type { RoomCallBinding } from '../calls/roomCalls.ts';
import { PersonIdentity } from '../ui/people.tsx';
import {
  allDayLabel,
  combineDateTime,
  dateParam,
  parseDateOnly,
  splitLocalIso,
  timeParam,
  wholeDayDuration,
  wholeDays,
  zonedDateParam,
  zonedEpoch,
} from './eventDates.ts';
import { durationMinutes, eventTimeLabel, formatDuration, toIsoDuration } from './layout.ts';
import {
  isMine,
  isOrganiser,
  myParticipant,
  type Identity as ViewerIdentity,
} from './participants.ts';
import { sourceThreadOf } from './sourceThread.ts';
import { useEscape } from './useEscape.ts';

/** Mirrors `mail/format.ts`'s own http(s)-only rule -- a join link is only
 *  ever a thing to click, so a `data:`/`javascript:` URI (or anything else
 *  a browser will not navigate) is never rendered as a link (agora#168
 *  review finding). */
const HTTP_URL_PATTERN = /^https?:\/\//i;

const RSVP_LABEL: Record<'accepted' | 'tentative' | 'declined', string> = {
  accepted: 'Yes',
  tentative: 'Maybe',
  declined: 'No',
};

const STATUS_PILL: Record<
  CalendarParticipationStatus,
  { label: string; tone?: 'good' | 'warn' | 'bad' }
> = {
  accepted: { label: 'Yes', tone: 'good' },
  tentative: { label: 'Maybe', tone: 'warn' },
  declined: { label: 'No', tone: 'bad' },
  'needs-action': { label: 'No answer' },
};

const NUMBER_WORD = [
  'No',
  'One',
  'Two',
  'Three',
  'Four',
  'Five',
  'Six',
  'Seven',
  'Eight',
  'Nine',
  'Ten',
];

/** "Four invited", not "4 invited": the list's heading is a sentence. */
/** A description's text with each http(s) address as a link that wraps
 *  inside the card rather than stretching it. */
function Linked({ text }: { text: string }) {
  return text.split(/(https?:\/\/[^\s<>]+)/gi).map((part, i) =>
    i % 2 === 1 ? (
      // biome-ignore lint/suspicious/noArrayIndexKey: a fixed split of one string, never reordered
      <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="cal-link">
        {part}
      </a>
    ) : (
      part
    ),
  );
}

function numberWord(n: number): string {
  return NUMBER_WORD[n] ?? String(n);
}

/** The frequencies the edit form offers, beyond whatever the event's own
 *  rule already reads -- `CalendarRecurrenceRule.frequency`'s own four
 *  values. `'none'` is never itself sent as a patch (see `saveEdit`'s own
 *  comment): it only ever labels a field that started, and stayed, without
 *  a rule at all. */
const REPEAT_FREQS = ['daily', 'weekly', 'monthly', 'yearly'] as const;
type RepeatFreq = 'none' | (typeof REPEAT_FREQS)[number];

const REPEAT_LABEL: Record<RepeatFreq, string> = {
  none: 'Does not repeat',
  daily: 'Every day',
  weekly: 'Every week',
  monthly: 'Every month',
  yearly: 'Every year',
};

/** What turning a call on or off does around the save. */
type CallChange = {
  patch: Partial<CalendarEventDraft>;
  /** Runs when the save fails. */
  undo?: () => Promise<void>;
  /** Runs once the save has succeeded. */
  commit?: () => Promise<void>;
};

/** The instant an event starts, read in the event's own time zone (the call
 *  window must not shift with the viewer's). */
function eventStartMs(event: Pick<CalendarEvent, 'start' | 'timeZone'>): number {
  const [day, time] = event.start.split('T');
  const zoned = day && time ? zonedEpoch(day, time.slice(0, 5), event.timeZone ?? null) : null;
  return zoned ?? new Date(event.start).getTime();
}

export function Event({
  client,
  mailClient,
  identity,
  calendars,
  agoraId,
  eventId,
  onClose,
  onError,
  calls,
  go,
}: {
  client: CalendarClient;
  mailClient: MailClient;
  identity: ViewerIdentity | null;
  calendars: readonly CalendarModel[];
  /** The open agora -- compared against the agora id a virtual location's
   *  own join link carries, so a link copied into the wrong agora's event
   *  is never treated as this event's own call (`isOwnCallLink`, agora#392).
   *  `parseEventCallUrl` matching the uid alone let an attendee's "Join
   *  call" button join this agora's room for an event that uid actually
   *  belonged to a different company's call -- an empty room, not the one
   *  anybody meant to reach. */
  agoraId: string;
  eventId: string;
  onClose: () => void;
  onError: (error: unknown) => void;
  /** Optional for the same reason `Room`'s own `calls` prop is (see its
   *  docblock): nothing in this app constructs a `CallsClient`/adapter/store
   *  yet, so without a host supplying one this draws no call action at all. */
  calls?: RoomCallBinding;
  /** Opens an address in the app; without it the thread card is a plain link. */
  go?: (path: string) => void;
}) {
  const [event, setEvent] = useState<CalendarEvent | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [rsvpBusy, setRsvpBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);

  // Propose a new time (agora#286): an attendee counters the organiser's own
  // slot with a `COUNTER`, sent as ordinary mail.
  const [proposing, setProposing] = useState(false);
  const [proposeStartDate, setProposeStartDate] = useState('');
  const [proposeStartTime, setProposeStartTime] = useState('');
  const [proposeEndDate, setProposeEndDate] = useState('');
  const [proposeEndTime, setProposeEndTime] = useState('');
  const [proposeNote, setProposeNote] = useState('');
  const [proposeBusy, setProposeBusy] = useState(false);
  const [proposeError, setProposeError] = useState<string | null>(null);
  /** Set once a `COUNTER` goes out; never cleared by a `DECLINECOUNTER`,
   *  since neither Outlook nor Google ever sends one (issue #286's own
   *  design limit) -- "You proposed …" is good for this page's lifetime,
   *  not until an answer arrives that may never come. */
  const [proposedSlot, setProposedSlot] = useState<{ start: Date; end: Date } | null>(null);

  // Edit an event (agora#158): organiser-only, title/time/place/repeat,
  // always against the whole series.
  const [editing, setEditing] = useState(false);
  /** The event the form is baselined against and saves to: the *master*,
   *  whenever the opened event is one occurrence of a series, never the
   *  occurrence itself -- see `openEdit`'s own comment. `null` until
   *  `openEdit` resolves it. */
  const [editSource, setEditSource] = useState<CalendarEvent | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editLocation, setEditLocation] = useState('');
  const [editAllDay, setEditAllDay] = useState(false);
  const [editStartDate, setEditStartDate] = useState('');
  const [editStartTime, setEditStartTime] = useState('');
  const [editEndDate, setEditEndDate] = useState('');
  const [editEndTime, setEditEndTime] = useState('');
  /** Whether the person has touched any date/time/all-day field since the
   *  form opened -- `saveEdit` only sends `start`/`duration` when this is
   *  true, so a title-only edit never moves the event, on a series opened
   *  from any one of its occurrences least of all (see `saveEdit`'s own
   *  comment). */
  const [editTimeTouched, setEditTimeTouched] = useState(false);
  const [editRepeat, setEditRepeat] = useState<RepeatFreq>('none');
  const [editCall, setEditCall] = useState(false);
  const [editScope, setEditScope] = useState<RecurrenceScope>('series');
  const [scopePrompt, setScopePrompt] = useState(false);
  const [editMayInviteOthers, setEditMayInviteOthers] = useState(false);
  const [editMayInviteSelf, setEditMayInviteSelf] = useState(false);
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Read through a ref, not the dependency list: a host that hands down a
  // new `onError` closure every render (an inline arrow, say) must not
  // refetch the event on every one of those renders -- it would momentarily
  // null `event` out and, along with it, tear down any call already under
  // way for it (agora#341 round 3).
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    let live = true;
    setEvent(null);
    setNotFound(false);
    setProposing(false);
    setProposedSlot(null);
    setProposeError(null);
    setEditing(false);
    setEditSource(null);
    setEditError(null);
    client
      .getEvent(eventId)
      .then((e) => {
        if (live) setEvent(e);
      })
      .catch((err: unknown) => {
        if (!live) return;
        setNotFound(true);
        onErrorRef.current(err);
      });
    return () => {
      live = false;
    };
  }, [client, eventId]);

  const [callLive, setCallLive] = useState(false);
  const [callError, setCallError] = useState<string | null>(null);
  /** Bumped once the event's call window opens, so the disabled Join button
   *  (agora#487) enables itself without the person having to do anything --
   *  it never read its own `callOpensAt > Date.now()` as anything but a
   *  one-time check before. */
  const [, setCallWindowOpened] = useState(0);

  // Keyed on the event's own id, not the whole `event` object -- an RSVP or
  // an edit save calls `setEvent` with a fresh object for the same id, and
  // that must not reset a call already under way the way opening a
  // different event does. `roomStatus` is what tells "nobody has started
  // this yet" apart from "someone already has, join them."
  const openEventId = event?.id ?? null;
  // The room key itself: `uid` (one room for a whole series, as its join link names), never `event.id` -- see `eventCallRoomId`'s own doc comment
  // (agora#341 round 3). Derived alongside `openEventId` rather than
  // replacing it: `openEventId` still answers "did the open event change at
  // all," which is what the switch-guards below need, while this is what
  // the call itself keys on.
  const openEventRoomId = event ? eventCallRoomId(event.uid) : null;

  // `Event` is reused across event switches rather than remounted (`Calendar`
  // renders it with no `key`, the same way `Room` is reused across room
  // switches), so anything async that started against one event has to
  // recognise landing on another one instead of touching that event's state.
  // Both refs are updated on every render rather than in an effect, so a
  // read of `.current` is never a render behind the value a callback closed
  // over -- the same pattern `Room.tsx` uses for `roomIdRef`/`callsRef`.
  const openEventIdRef = useRef(openEventId);
  openEventIdRef.current = openEventId;
  const callsRef = useRef(calls);
  callsRef.current = calls;

  // The call itself lives above the routes (`../calls/CallSession.tsx`), so
  // leaving this event never ends it; this page only shows it as joined.
  const session = useCallSession();
  const onThisCall = Boolean(openEventRoomId) && session?.call?.roomId === openEventRoomId;
  const onOtherCall = Boolean(session?.call) && !onThisCall;
  const callFocused = onThisCall && Boolean(session?.focused);
  const callStarting = session?.starting ?? false;

  // An event switch while someone else's call is already running there
  // should offer "Join call," not "Start call" -- `roomStatus` is the one
  // read that tells the two apart. Depends on `openEventId` and whether
  // `calls` is present at all, never on `calls`'s own identity (read through
  // `callsRef`): a host that hands down a fresh `calls` object on every
  // render never re-fires this and never drops a call already in progress,
  // but a host that only has `calls` ready a render or two after the first
  // one still gets this read once it arrives, rather than being stuck
  // offering "Start call" for an event someone is already on.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see above -- `calls` is read through `callsRef`; only its presence, not its identity, belongs in this list.
  useEffect(() => {
    setCallError(null);
    setCallLive(false);
    const calls = callsRef.current;
    if (!calls || !openEventId || !openEventRoomId) return;
    let live = true;
    calls.client.roomStatus(openEventRoomId).then(
      (status) => {
        if (live) setCallLive(status.live);
      },
      () => {
        // A failed read just means the button defaults to "Start call" --
        // the click itself re-checks and surfaces a real error if the
        // service is actually unreachable.
      },
    );
    return () => {
      live = false;
    };
  }, [openEventId, openEventRoomId, Boolean(calls)]);

  // The Join button for an event's own call stays disabled until
  // `callOpensAt`, computed below at render time from `event`. Nothing else
  // re-rendered this page once that moment passed, so the button stayed
  // disabled with no event to enable it (agora#487) -- this is the timer
  // that does. Recomputed, not read off `callOpensAt` itself: that is
  // declared later in the render body, after the early returns above, where
  // a hook cannot go.
  useEffect(() => {
    if (!event) return;
    const opensAt = eventCallWindow(
      eventStartMs(event),
      eventStartMs(event) + durationMinutes(event.duration) * 60_000,
    ).validFrom;
    let timer: ReturnType<typeof setTimeout>;
    const schedule = () => {
      const remaining = opensAt - Date.now();
      if (remaining <= 0) {
        setCallWindowOpened((n) => n + 1);
        return;
      }
      // A delay past ~24.8 days overflows `setTimeout`'s 32-bit signed
      // argument and fires at once -- capping it well under that and
      // rechecking keeps a call window weeks out from opening the button
      // early.
      timer = setTimeout(schedule, Math.min(remaining, 24 * 60 * 60 * 1000));
    };
    schedule();
    return () => clearTimeout(timer);
  }, [event]);

  const startCall = useCallback(() => {
    const calls = callsRef.current;
    if (!calls || !session || !event || !openEventRoomId || onThisCall || onOtherCall) return;
    const startedEventId = event.id;
    const ownLink = (event.virtualLocations ?? []).some((v) => {
      const parsed = parseEventCallUrl(v.uri);
      return parsed !== null && parsed.uid === event.uid && parsed.agoraId === agoraId;
    });
    setCallError(null);
    void session
      .start(
        {
          roomId: openEventRoomId,
          title: event.title,
          path: window.location.pathname,
          // An event that carries its call link has hours of its own; an
          // ad-hoc call from an event without one is a huddle.
          ...(ownLink
            ? {
                window: eventCallWindow(
                  eventStartMs(event),
                  eventStartMs(event) + durationMinutes(event.duration) * 60_000,
                ),
              }
            : {}),
        },
        calls,
      )
      .then((result) => {
        if (!result.ok && openEventIdRef.current === startedEventId) setCallError(result.error);
      });
  }, [session, event, openEventRoomId, onThisCall, onOtherCall, agoraId]);

  const rsvp = useCallback(
    (status: 'accepted' | 'tentative' | 'declined') => {
      if (!event || rsvpBusy) return;
      const mine = myParticipant(event, identity);
      if (!mine) return;
      const participants = (event.participants ?? []).map((p) =>
        p === mine ? { ...p, participationStatus: status } : p,
      );
      setRsvpBusy(true);
      client
        .updateEvent(event.id, { participants }, { sendSchedulingMessages: true })
        .then(setEvent)
        .catch(onError)
        .finally(() => setRsvpBusy(false));
    },
    [client, event, identity, rsvpBusy, onError],
  );

  const remove = useCallback(() => {
    if (!event || deleteBusy) return;
    setDeleteBusy(true);
    // The call and its guest link end first: once the event is gone its
    // `agora-call:v1` ids are gone too, and nobody could revoke the link.
    const guest = parseGuestEntry(event.virtualLocations);
    const current = callsRef.current;
    const ended =
      guest && current
        ? endEventCall(current.client, guest).then(
            () => true,
            (endError: unknown) => {
              console.error('Could not end the call of the event:', endError);
              return false;
            },
          )
        : Promise.resolve(true);
    ended
      .then(async (callEnded) => {
        await client.deleteEvent(event.id, { sendSchedulingMessages: true });
        if (!callEnded)
          onError(new Error('The event was deleted, but its call could not be ended.'));
        onClose();
      })
      .catch((err: unknown) => {
        setConfirmDelete(false);
        onError(err);
      })
      .finally(() => setDeleteBusy(false));
  }, [client, event, deleteBusy, onClose, onError]);

  // Escape backs out one step: the edit form, then the proposal, then the
  // event itself -- never while a call is on, where it is not this screen's.
  useEscape(!onThisCall, () => {
    if (editing) {
      setEditing(false);
      setEditError(null);
    } else if (proposing) {
      setProposing(false);
      setProposeError(null);
    } else onClose();
  });

  if (notFound) {
    return (
      <Panes.Column flex>
        <div className="flex flex-1 flex-col gap-4 min-h-0 overflow-y-auto box-border stream cal-stream">
          <Empty icon="calendar">This event could not be found.</Empty>
          <div>
            <Button kind="default" onPress={onClose}>
              Back to the week
            </Button>
          </div>
        </div>
      </Panes.Column>
    );
  }

  if (!event) {
    return (
      <Panes.Column flex>
        <div className="flex flex-1 flex-col min-h-0 overflow-y-auto box-border stream cal-stream">
          <p className="text-sm text-muted" role="status">
            Loading the event…
          </p>
        </div>
      </Panes.Column>
    );
  }

  const start = new Date(event.start);
  const minutes = durationMinutes(event.duration);
  const end = new Date(start.getTime() + minutes * 60_000);
  const mine = myParticipant(event, identity);
  const canRsvp = mine !== undefined && !isOrganiser(mine);
  const organiser = event.participants?.find(isOrganiser);
  const calendar = calendars.find((c) => c.id === event.calendarId);
  const canDelete = calendar?.myRights.includes('mayDelete') ?? false;
  /** Only the organiser sees Edit. An event that names one (any participant
   *  with `isOrganiser`'s own "chair" or "owner" role) is only ever the
   *  organiser's to change -- an attendee's own copy, landed on their
   *  calendar by the invite, is still not theirs to rewrite, even though
   *  they hold full rights on the calendar it sits on (the same rights
   *  `canDelete` reads). An event with no participants at all names nobody,
   *  organiser or attendee -- a plain entry on your own calendar -- so that
   *  case falls back to the calendar's own rights instead. */
  const canEdit = organiser
    ? Boolean(mine && isOrganiser(mine))
    : (calendar?.myRights.includes('mayWriteAll') ?? false);
  const isAllDay = event.isAllDay;
  const toldCount = (event.participants ?? []).filter((p) => !isMine(p, identity)).length;
  const allDayDays = isAllDay ? wholeDays(event.duration) : 1;
  // agora#163 (MSG-060): the first join link, if a call ever writes one --
  // only when its scheme is one a browser will actually navigate
  // (`HTTP_URL_PATTERN`'s own comment).
  // An Agora call link for this very event is the "Join call" action below,
  // not a second plain link -- but only when it also names *this* agora
  // (agora#392): a link copied from a different company's event, even one
  // that happens to carry the same uid, must fall through to the plain
  // join-link branch below rather than joining this agora's own, unrelated
  // room for it.
  const isOwnCallLink = (uri: string) => {
    const parsed = parseEventCallUrl(uri);
    return parsed !== null && parsed.uid === event.uid && parsed.agoraId === agoraId;
  };
  const thread = sourceThreadOf(event, agoraId);
  const callOpensAt = eventCallWindow(
    eventStartMs(event),
    eventStartMs(event) + minutes * 60_000,
  ).validFrom;
  const hasEventCall = event.virtualLocations?.some((v) => isOwnCallLink(v.uri)) ?? false;
  const rawJoinLink = event.virtualLocations?.find((v) => !isOwnCallLink(v.uri));
  const joinLink = rawJoinLink && HTTP_URL_PATTERN.test(rawJoinLink.uri) ? rawJoinLink : undefined;
  // The description of an event made here repeats its call's link ("Join:
  // <link>", for calendars that cannot show a virtual location); with the
  // call card above, a line that is only that link is noise.
  const descriptionText = (event.description ?? '')
    .split('\n')
    .filter(
      (line) => !(calls && hasEventCall && isOwnCallLink(line.replace(/^\s*Join:\s*/i, '').trim())),
    )
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  const openPropose = () => {
    setProposeStartDate(dateParam(start));
    setProposeStartTime(timeParam(start));
    setProposeEndDate(dateParam(end));
    setProposeEndTime(timeParam(end));
    setProposeNote('');
    setProposeError(null);
    setProposing(true);
  };

  const cancelPropose = () => {
    setProposing(false);
    setProposeError(null);
  };

  const sendPropose = async () => {
    if (!mine || !organiser || proposeBusy) return;
    const newStart = combineDateTime(proposeStartDate, proposeStartTime);
    const newEnd = combineDateTime(proposeEndDate, proposeEndTime);
    if (!newStart || !newEnd) {
      setProposeError('Give both a day and a time.');
      return;
    }
    setProposeBusy(true);
    setProposeError(null);
    try {
      const proposal = counter({
        uid: event.uid,
        sequence: event.sequence,
        ...(event.recurrenceId ? { recurrenceId: new Date(event.recurrenceId) } : {}),
        summary: event.title,
        organizer: { email: organiser.calendarAddress, name: organiser.name },
        attendee: { email: mine.calendarAddress, name: mine.name },
        start: newStart,
        end: newEnd,
        ...(proposeNote.trim() ? { comment: proposeNote.trim() } : {}),
      });
      const mailIdentities = await fetchMailIdentities(mailClient);
      const from = mailIdentities[0];
      if (!from) throw new Error('no sending identity on this account');
      const note = proposeNote.trim();
      await sendMail(mailClient, {
        identityId: from.id,
        to: [{ email: organiser.calendarAddress, name: organiser.name }],
        subject: `New time: ${event.title}`,
        text: note
          ? `I proposed a new time for "${event.title}": ${eventTimeLabel(newStart, newEnd)}.\n\n${note}`
          : `I proposed a new time for "${event.title}": ${eventTimeLabel(newStart, newEnd)}.`,
        calendar: proposal,
      });
      setProposedSlot({ start: newStart, end: newEnd });
      setProposing(false);
    } catch (err) {
      setProposeError('Could not send the proposal.');
      onError(err);
    } finally {
      setProposeBusy(false);
    }
  };

  /** Opens the edit form -- against the *master*, never the occurrence
   *  `eventId` happens to be, whenever the opened event is a synthetic
   *  occurrence (`recurrenceId` set): its own `start` is that one
   *  occurrence's date, and baselining the form on it is exactly the bug a
   *  refuter caught -- editing only the title from a later occurrence
   *  silently re-anchored (and truncated) the whole series onto that
   *  occurrence's own date once `saveEdit` sent it back with `scope:
   *  'series'`. Loading the master first means the day the form opens on,
   *  and the day nothing moves to when it is left untouched, is always the
   *  series' own day (`saveEdit`'s own `editTimeTouched` gate is the other
   *  half of that fix). */
  const openEdit = async (scope: RecurrenceScope = 'series') => {
    let source = event;
    setScopePrompt(false);
    if (scope === 'series' && event.recurrenceId !== undefined) {
      const masterId = event.baseEventId ?? event.id;
      if (masterId !== event.id) {
        try {
          source = await client.getEvent(masterId);
        } catch (err) {
          onError(err);
          return;
        }
      }
    }
    setEditScope(scope);
    setEditSource(source);
    setEditTitle(source.title);
    setEditLocation(source.location ?? '');
    setEditAllDay(source.isAllDay);
    setEditMayInviteOthers(source.mayInviteOthers ?? false);
    setEditMayInviteSelf(source.mayInviteSelf ?? false);
    const { date: startDate, time: startTime } = splitLocalIso(source.start);
    if (source.isAllDay) {
      const days = wholeDays(source.duration);
      const startDay = parseDateOnly(startDate);
      setEditStartDate(startDate);
      setEditEndDate(
        startDay
          ? dateParam(
              new Date(startDay.getFullYear(), startDay.getMonth(), startDay.getDate() + days - 1),
            )
          : startDate,
      );
      setEditStartTime('');
      setEditEndTime('');
    } else {
      // Both the master's own duration and the end this reads back into the
      // form are computed in the event's zone, never the browser's --
      // `zonedEpoch`'s own doc comment says why a plain `Date` cannot do
      // this once the two zones differ and a daylight-saving change sits
      // between the two times.
      const startEpoch = zonedEpoch(startDate, startTime, source.timeZone);
      const sourceMinutes = durationMinutes(source.duration);
      const endFields =
        startEpoch === null
          ? { date: startDate, time: startTime }
          : zonedDateParam(startEpoch + sourceMinutes * 60_000, source.timeZone);
      setEditStartDate(startDate);
      setEditStartTime(startTime);
      setEditEndDate(endFields.date);
      setEditEndTime(endFields.time);
    }
    setEditRepeat(source.recurrenceRule?.frequency ?? 'none');
    setEditCall(
      source.virtualLocations?.some((v) => isOwnCallLink(v.uri)) === true &&
        parseGuestEntry(source.virtualLocations) !== null,
    );
    setEditTimeTouched(false);
    setEditError(null);
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setEditError(null);
  };

  /** Marks a date/time (or all-day) field as touched, alongside setting it --
   *  the only thing `saveEdit` asks before it will send `start`/`duration`
   *  at all (see its own comment). */
  const touchTime = () => setEditTimeTouched(true);

  /**
   * What turning "Add a call" on or off changes on the event: on makes the
   * session, the member link and the guest link; off revokes the guest link
   * and ends the session. A repeating event or one occurrence of it is left
   * alone: one session cannot serve a series.
   */
  const callChange = async (
    source: CalendarEvent,
    edited: Partial<CalendarEventDraft>,
  ): Promise<CallChange | null> => {
    const current = callsRef.current;
    if (!current || source.recurrenceRule !== undefined || editScope !== 'series') return null;
    const entries = source.virtualLocations ?? [];
    const guest = parseGuestEntry(entries);
    if (editCall && !guest) {
      // The window follows the times this save writes, not the old ones.
      const startMs = eventStartMs({
        start: edited.start ?? source.start,
        timeZone: source.timeZone,
      });
      const endMs = startMs + durationMinutes(edited.duration ?? source.duration) * 60_000;
      const setup = await createEventCall(
        current.client,
        window.location.origin,
        source.uid,
        startMs,
        endMs,
      );
      const member = eventCallUrl(window.location.origin, agoraId, source.uid);
      const kept = entries.filter((v) => !isOwnCallLink(v.uri));
      const lines = [`Join: ${member}`, `Guests: ${setup.guestUrl}`];
      return {
        patch: {
          virtualLocations: [...kept, { uri: member }, setup.guestEntry],
          description: [source.description?.trim(), ...lines].filter(Boolean).join('\n\n'),
        },
        // The save failed: the session just made has no event to serve.
        undo: () =>
          current.client
            .endSession(setup.sessionId, { requestId: crypto.randomUUID() })
            .then(() => undefined)
            .catch((endError: unknown) =>
              console.error('Could not end the call made for an unsaved event:', endError),
            ),
      };
    }
    if (!editCall && guest) {
      const kept = entries.filter((v) => !isOwnCallLink(v.uri) && v.uri !== guest.uri);
      const gone = (line: string) =>
        /^\s*(Join|Guests):\s*/i.test(line) &&
        (isOwnCallLink(line.replace(/^\s*Join:\s*/i, '').trim()) || line.includes(guest.uri));
      return {
        patch: {
          virtualLocations: kept,
          description: (source.description ?? '')
            .split('\n')
            .filter((line) => !gone(line))
            .join('\n')
            .trim(),
        },
        // Only once the event no longer carries the link.
        commit: () => endEventCall(current.client, guest),
      };
    }
    return null;
  };

  const saveEdit = async () => {
    if (!editSource || editBusy) return;
    const title = editTitle.trim();
    if (!title) {
      setEditError('Give it a title.');
      return;
    }
    const patch: Partial<CalendarEventDraft> = {
      title,
      // Always sent, even empty: `updateEvent`'s own patch skips a field
      // entirely at `undefined` (its contract is "untouched", not
      // "cleared"), so a location the organiser deleted down to nothing
      // still has to arrive as `''` to actually clear it, not be dropped
      // from the patch and left as it was.
      location: editLocation.trim(),
      // Keep the event's own time zone -- this form never offers to change
      // it, so every save carries it forward rather than risking a patch
      // that a caller reads as "leave it alone" only by accident.
      timeZone: editSource.timeZone,
      isAllDay: editAllDay,
      mayInviteOthers: editMayInviteOthers,
      mayInviteSelf: editMayInviteSelf,
    };
    // `start`/`duration` only when the person actually touched a date/time
    // field or the all-day toggle -- never on a title-only edit. Before this
    // gate existed, every save recomputed both from whatever the fields last
    // held, which -- combined with `openEdit` baselining on whichever
    // occurrence happened to be open, now also fixed -- is what silently
    // re-anchored a whole series onto that occurrence's own date.
    if (editTimeTouched) {
      if (editAllDay) {
        const startDay = parseDateOnly(editStartDate);
        const endDay = parseDateOnly(editEndDate);
        if (!startDay || !endDay || endDay < startDay) {
          setEditError('Give a start day, and an end day on or after it.');
          return;
        }
        patch.start = `${editStartDate}T00:00:00`;
        patch.duration = wholeDayDuration(startDay, endDay);
      } else {
        // Both wall-clock times are read in the event's own zone, never the
        // browser's -- a plain `Date` built straight from the typed fields
        // is wrong the moment the two differ and a daylight-saving change
        // sits between them (`zonedEpoch`'s own doc comment).
        const startEpoch = zonedEpoch(editStartDate, editStartTime, editSource.timeZone);
        const endEpoch = zonedEpoch(editEndDate, editEndTime, editSource.timeZone);
        if (startEpoch === null || endEpoch === null || endEpoch <= startEpoch) {
          setEditError('Give a start and an end after it.');
          return;
        }
        patch.start = `${editStartDate}T${editStartTime}:00`;
        patch.duration = toIsoDuration(Math.round((endEpoch - startEpoch) / 60_000));
      }
    }
    // `'none'` never becomes a patch: `@wtfalch/calendar` 0.2.0's own
    // `updateEvent` has no way to clear an existing `recurrenceRule` (logged
    // as a Finding, not fixed here -- it lives in a different repository).
    // Reachable only when the event already carries no rule, in which case
    // the baseline already reads `'none'` and this simply never fires.
    const baseline: RepeatFreq = editSource.recurrenceRule?.frequency ?? 'none';
    if (editScope === 'series' && editRepeat !== baseline && editRepeat !== 'none') {
      patch.recurrenceRule = { ...(editSource.recurrenceRule ?? {}), frequency: editRepeat };
    }
    // Attendees get the update -- everyone on the series' own invite list
    // besides me.
    const sendSchedulingMessages = (editSource.participants ?? []).some(
      (p) => !isMine(p, identity),
    );
    setEditBusy(true);
    setEditError(null);
    let call: CallChange | null = null;
    try {
      call = await callChange(editSource, patch);
      if (call) Object.assign(patch, call.patch);
    } catch (callError) {
      setEditError('Could not change the call. The event was not saved.');
      onError(callError);
      setEditBusy(false);
      return;
    }
    try {
      // A series edit goes to the master's own id (`editSource.id`, never the
      // occurrence `eventId` may have opened on); an occurrence or a
      // following edit goes to the occurrence itself, `editSource` being it.
      await client.updateEvent(editSource.id, patch, {
        sendSchedulingMessages,
        scope: editScope,
      });
      if (call?.commit) {
        await call.commit().catch((endError: unknown) => {
          console.error('Could not end the call after saving the event:', endError);
          onError(new Error('The event was saved, but its call could not be ended.'));
        });
      }
      if (editScope !== 'series') {
        // An occurrence's id is positional and these writes renumber the
        // later ones, so go back to the week rather than re-read a stale id.
        setEditing(false);
        onClose();
        return;
      }
      // Re-read through the id this page actually opened on -- an occurrence
      // or the master alike -- so the view goes back to showing the same
      // thing you were looking at, now current, rather than jumping to the
      // master's own copy.
      const refreshed = await client.getEvent(event.id);
      setEvent(refreshed);
      setEditing(false);
    } catch (err) {
      await call?.undo?.();
      setEditError('Could not save the changes.');
      onError(err);
    } finally {
      setEditBusy(false);
    }
  };

  if (editing && editSource) {
    const isRecurring = editSource.recurrenceRule !== undefined;
    const hadCall = parseGuestEntry(editSource.virtualLocations) !== null;
    const repeatOptions: readonly RepeatFreq[] = editSource.recurrenceRule
      ? REPEAT_FREQS
      : ['none', ...REPEAT_FREQS];
    const browserZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const showZoneNote =
      !editAllDay && editSource.timeZone !== null && editSource.timeZone !== browserZone;

    return (
      <Panes.Column flex>
        {calls && <CallSurface roomId={openEventRoomId ?? ''} className="room-call-bar" />}
        <div className="flex flex-1 flex-col min-h-0 overflow-y-auto box-border stream cal-stream">
          <div className="cal-find">
            <div className="flex flex-col gap-1 col-head">
              <h1 className="h1">Edit event</h1>
              {editScope === 'occurrence' && (
                <p className="lead m-0 text-lg text-muted">Changes apply to this event only.</p>
              )}
              {editScope === 'following' && (
                <p className="lead m-0 text-lg text-muted">
                  Changes apply to this and the events after it.
                </p>
              )}
              {editScope === 'series' && isRecurring && (
                <p className="lead m-0 text-lg text-muted">Changes apply to the whole series.</p>
              )}
            </div>
            <div className="cal-find-form">
              <Field label="Title">
                {(field) => (
                  <Input
                    {...field}
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    block
                  />
                )}
              </Field>
              <Field label="Location">
                {(field) => (
                  <Input
                    {...field}
                    value={editLocation}
                    onChange={(e) => setEditLocation(e.target.value)}
                    block
                  />
                )}
              </Field>
              <Toggle
                label="Guests can invite others"
                checked={editMayInviteOthers}
                onChange={setEditMayInviteOthers}
              />
              <Toggle
                label="Anyone can join"
                checked={editMayInviteSelf}
                onChange={setEditMayInviteSelf}
              />
              <Checkbox
                label="All day"
                checked={editAllDay}
                onChange={(v) => {
                  setEditAllDay(v);
                  touchTime();
                }}
              />
              {editAllDay ? (
                <div className="flex gap-4 flex-wrap cal-find-row">
                  <Field label="Start day">
                    {(field) => (
                      <Input
                        {...field}
                        type="date"
                        value={editStartDate}
                        onChange={(e) => {
                          setEditStartDate(e.target.value);
                          touchTime();
                        }}
                        block
                      />
                    )}
                  </Field>
                  <Field label="End day">
                    {(field) => (
                      <Input
                        {...field}
                        type="date"
                        value={editEndDate}
                        onChange={(e) => {
                          setEditEndDate(e.target.value);
                          touchTime();
                        }}
                        block
                      />
                    )}
                  </Field>
                </div>
              ) : (
                <>
                  <div className="flex gap-4 flex-wrap cal-find-row">
                    <Field label="Start day">
                      {(field) => (
                        <Input
                          {...field}
                          type="date"
                          value={editStartDate}
                          onChange={(e) => {
                            setEditStartDate(e.target.value);
                            touchTime();
                          }}
                          block
                        />
                      )}
                    </Field>
                    <Field label="Start time">
                      {(field) => (
                        <Input
                          {...field}
                          type="time"
                          value={editStartTime}
                          onChange={(e) => {
                            setEditStartTime(e.target.value);
                            touchTime();
                          }}
                          block
                        />
                      )}
                    </Field>
                  </div>
                  <div className="flex gap-4 flex-wrap cal-find-row">
                    <Field label="End day">
                      {(field) => (
                        <Input
                          {...field}
                          type="date"
                          value={editEndDate}
                          onChange={(e) => {
                            setEditEndDate(e.target.value);
                            touchTime();
                          }}
                          block
                        />
                      )}
                    </Field>
                    <Field label="End time">
                      {(field) => (
                        <Input
                          {...field}
                          type="time"
                          value={editEndTime}
                          onChange={(e) => {
                            setEditEndTime(e.target.value);
                            touchTime();
                          }}
                          block
                        />
                      )}
                    </Field>
                  </div>
                  {showZoneNote && (
                    <p className="text-xs text-muted">Times are shown in {editSource.timeZone}.</p>
                  )}
                </>
              )}
              {calls &&
                editScope === 'series' &&
                (isRecurring ? (
                  <p className="text-xs text-muted">
                    A call cannot be added to a repeating event yet: each time it repeats would need
                    its own call and its own link.
                  </p>
                ) : (
                  <>
                    <Toggle label="Add a call" checked={editCall} onChange={setEditCall} />
                    {editCall && editTimeTouched && hadCall && (
                      <p className="text-xs text-muted">
                        The call keeps the hours it was made for, and so does its link.
                      </p>
                    )}
                  </>
                ))}
              {editScope === 'series' && (
                <Field label="Repeats">
                  <Select
                    block
                    value={editRepeat}
                    onChange={(e) => setEditRepeat(e.target.value as RepeatFreq)}
                  >
                    {repeatOptions.map((freq) => (
                      <option key={freq} value={freq}>
                        {REPEAT_LABEL[freq]}
                      </option>
                    ))}
                  </Select>
                </Field>
              )}
            </div>
            {editError && (
              <p className="cal-error text-bad" role="alert">
                {editError}
              </p>
            )}
            <div className="flex gap-3 items-center cal-find-actions">
              <Button kind="primary" busy={editBusy} onPress={() => void saveEdit()}>
                Save
              </Button>
              <Button kind="default" onPress={cancelEdit}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </Panes.Column>
    );
  }

  return (
    <Panes.Column flex>
      <div className="cal-event-head">
        <Button kind="ghost" size="sm" onPress={onClose}>
          <Icon name="chev-left" size={16} />
          The week
        </Button>
        <div className="flex-1 cal-grow" />
        {/* One way in: an event with its own call card has the card's Join
            button, so this icon is only for an event without one. */}
        {calls && !hasEventCall && (
          <Button
            kind="ghost"
            size="sm"
            iconOnly
            aria-label={
              onOtherCall
                ? 'Leave the call you are on first'
                : onThisCall
                  ? 'In this call'
                  : callLive
                    ? 'Join call'
                    : 'Start call'
            }
            busy={callStarting}
            disabled={callStarting || onThisCall || onOtherCall}
            onPress={startCall}
          >
            <Icon name="phone" size={18} />
          </Button>
        )}
        {(canEdit || canDelete) && (
          <Menu
            label="Event actions"
            placement="bottom end"
            trigger={
              <Button kind="ghost" size="sm" iconOnly aria-label="More">
                <Icon name="more" size={18} />
              </Button>
            }
            items={[
              ...(canEdit
                ? [
                    {
                      id: 'edit',
                      label: 'Edit event',
                      icon: 'pen' as const,
                      onAction: () =>
                        event.recurrenceId !== undefined ? setScopePrompt(true) : void openEdit(),
                    },
                  ]
                : []),
              ...(canDelete
                ? [
                    {
                      id: 'delete',
                      label: 'Delete event',
                      icon: 'trash' as const,
                      danger: true,
                      onAction: () => setConfirmDelete(true),
                    },
                  ]
                : []),
            ]}
          />
        )}
      </div>
      {callError && (
        <Callout tone="bad" timed onDismiss={() => setCallError(null)}>
          {callError}
        </Callout>
      )}
      {calls && (
        <CallSurface
          roomId={openEventRoomId ?? ''}
          className={callFocused ? 'room-call-grid flex-1 min-h-0' : 'room-call-bar'}
        />
      )}
      {!(calls && callFocused) && (
        <div className="flex flex-1 flex-col min-h-0 overflow-y-auto box-border stream cal-stream">
          <div className="flex items-start cal-event">
            <div className="cal-event-left">
              <div className="flex gap-2 flex-wrap cal-event-chips">
                {organiser && !isOrganiser(mine ?? organiser) && (
                  <Pill outline>{organiser.name ?? organiser.calendarAddress} invited you</Pill>
                )}
                {mine &&
                  (mine.participationStatus === undefined ||
                  mine.participationStatus === 'needs-action' ? (
                    <Pill tone="warn">
                      <i className="cal-dot" aria-hidden="true" />
                      You have not answered
                    </Pill>
                  ) : (
                    <Pill tone={STATUS_PILL[mine.participationStatus].tone}>
                      {`You said ${STATUS_PILL[mine.participationStatus].label.toLowerCase()}`}
                    </Pill>
                  ))}
              </div>
              <h1 className="h1">{event.title}</h1>
              <p className="lead m-0 text-lg text-muted">
                {isAllDay
                  ? allDayLabel(start, allDayDays)
                  : `${eventTimeLabel(start, end)} · ${formatDuration(minutes)}`}
              </p>
              {event.location && <p className="text-sm m-0 text-muted">{event.location}</p>}
              {calls && hasEventCall && !onThisCall && (
                <section className="card mb-0 flex items-center gap-3 cal-call">
                  <Icon name="phone" size={20} />
                  <span className="flex flex-col flex-1 gap-1">
                    <span className="text-sm font-strong">A call for this event</span>
                    <span className="text-xs text-muted">
                      Opens here, in Agora. Anybody who is invited can walk in.
                    </span>
                  </span>
                  <Button
                    kind="primary"
                    size="sm"
                    busy={callStarting}
                    disabled={callStarting || onOtherCall || callOpensAt > Date.now()}
                    onPress={startCall}
                  >
                    Join call
                  </Button>
                  {callOpensAt > Date.now() && (
                    <span className="text-xs text-muted">
                      Opens at{' '}
                      {new Date(callOpensAt).toLocaleTimeString([], { timeStyle: 'short' })}
                    </span>
                  )}
                  {onOtherCall && (
                    <span className="text-xs text-muted">Leave the call you are on first</span>
                  )}
                </section>
              )}
              {joinLink && (
                <p className="text-sm m-0 text-muted">
                  <a href={joinLink.uri} target="_blank" rel="noopener noreferrer">
                    {joinLink.name ?? 'Join'}
                  </a>
                </p>
              )}
              {thread && (
                <section className="card mb-0 flex items-center gap-3 cal-thread">
                  <Icon name={thread.kind === 'mail' ? 'mail' : 'chat'} size={20} />
                  <span className="flex flex-col flex-1 gap-1 min-w-0">
                    <span className="text-xs text-muted">
                      {thread.kind === 'mail'
                        ? 'It came from a mail thread'
                        : 'It came from a chat'}
                    </span>
                    <span className="text-sm font-strong truncate">{thread.title}</span>
                  </span>
                  <Button asChild size="sm">
                    <a
                      href={thread.path}
                      onClick={(e) => {
                        if (!go || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                        e.preventDefault();
                        go(thread.path);
                      }}
                    >
                      Open
                    </a>
                  </Button>
                </section>
              )}
              {descriptionText && (
                <section className="card mb-0 flex flex-col gap-3 cal-card">
                  <h2 className="h3">What it is for</h2>
                  <p className="text-sm">
                    <Linked text={descriptionText} />
                  </p>
                </section>
              )}
            </div>
            {(canRsvp || (event.participants && event.participants.length > 0)) && (
              <aside className="cal-event-aside pt-3">
                {canRsvp && (
                  <div className="flex flex-col gap-2 mb-5 cal-rsvp">
                    <span className="text-sm font-bold">Going?</span>
                    <div className="flex gap-2 cal-rsvp-buttons">
                      {(['accepted', 'tentative', 'declined'] as const).map((status) => (
                        <Button
                          key={status}
                          kind={mine?.participationStatus === status ? 'primary' : 'default'}
                          size="sm"
                          aria-pressed={mine?.participationStatus === status}
                          busy={rsvpBusy}
                          onPress={() => rsvp(status)}
                        >
                          {RSVP_LABEL[status]}
                        </Button>
                      ))}
                    </div>
                    {proposedSlot ? (
                      <Pill>
                        You proposed {eventTimeLabel(proposedSlot.start, proposedSlot.end)}
                      </Pill>
                    ) : (
                      organiser &&
                      !proposing && (
                        <Button kind="default" size="sm" onPress={openPropose}>
                          Propose a new time
                        </Button>
                      )
                    )}
                    {proposing && organiser && (
                      <div className="card cal-tint cal-find-form">
                        <div className="flex gap-4 flex-wrap cal-find-row">
                          <Field label="Start day">
                            {(field) => (
                              <Input
                                {...field}
                                type="date"
                                value={proposeStartDate}
                                onChange={(e) => setProposeStartDate(e.target.value)}
                                block
                              />
                            )}
                          </Field>
                          <Field label="Start time">
                            {(field) => (
                              <Input
                                {...field}
                                type="time"
                                value={proposeStartTime}
                                onChange={(e) => setProposeStartTime(e.target.value)}
                                block
                              />
                            )}
                          </Field>
                        </div>
                        <div className="flex gap-4 flex-wrap cal-find-row">
                          <Field label="End day">
                            {(field) => (
                              <Input
                                {...field}
                                type="date"
                                value={proposeEndDate}
                                onChange={(e) => setProposeEndDate(e.target.value)}
                                block
                              />
                            )}
                          </Field>
                          <Field label="End time">
                            {(field) => (
                              <Input
                                {...field}
                                type="time"
                                value={proposeEndTime}
                                onChange={(e) => setProposeEndTime(e.target.value)}
                                block
                              />
                            )}
                          </Field>
                        </div>
                        <Field label="Note (optional)">
                          {(field) => (
                            <Textarea
                              {...field}
                              value={proposeNote}
                              onChange={(e) => setProposeNote(e.target.value)}
                              rows={2}
                            />
                          )}
                        </Field>
                        <div className="flex gap-3 items-center cal-find-actions">
                          <Button
                            kind="primary"
                            size="sm"
                            busy={proposeBusy}
                            onPress={() => void sendPropose()}
                          >
                            Send
                          </Button>
                          <Button kind="default" size="sm" onPress={cancelPropose}>
                            Cancel
                          </Button>
                        </div>
                        {proposeError && (
                          <p className="cal-error text-bad" role="alert">
                            {proposeError}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {event.participants && event.participants.length > 0 && (
                  <div className="flex flex-col gap-1 cal-invited">
                    <span className="up">{numberWord(event.participants.length)} invited</span>
                    {event.participants.map((p: CalendarParticipant) => (
                      <div
                        key={p.calendarAddress}
                        className="flex items-center justify-between gap-2 py-1 px-0 cal-invited-row"
                      >
                        <PersonIdentity
                          name={p.name}
                          address={p.calendarAddress}
                          email={p.calendarAddress}
                          kind="line"
                          size="sm"
                        />
                        <Pill
                          outline={
                            isOrganiser(p) ||
                            !STATUS_PILL[p.participationStatus ?? 'needs-action'].tone
                          }
                          tone={
                            isOrganiser(p)
                              ? undefined
                              : STATUS_PILL[p.participationStatus ?? 'needs-action'].tone
                          }
                        >
                          {isOrganiser(p)
                            ? 'Organiser'
                            : STATUS_PILL[p.participationStatus ?? 'needs-action'].label}
                        </Pill>
                      </div>
                    ))}
                  </div>
                )}
              </aside>
            )}
          </div>
        </div>
      )}
      {scopePrompt && (
        <Modal
          title="Edit which events?"
          onClose={() => setScopePrompt(false)}
          footer={
            <Button kind="ghost" onPress={() => setScopePrompt(false)}>
              Cancel
            </Button>
          }
        >
          <div className="flex flex-col gap-2">
            <Button block onPress={() => void openEdit('occurrence')}>
              This event
            </Button>
            {/* "This and following" waits on @wtfalch/calendar: its updateFollowing cuts the old series before it creates the new one, with no rollback. */}
            <Button block onPress={() => void openEdit('series')}>
              All events
            </Button>
          </div>
        </Modal>
      )}
      {confirmDelete && (
        <Modal
          title="Delete this event?"
          onClose={deleteBusy ? undefined : () => setConfirmDelete(false)}
          closeDisabled={deleteBusy}
          footer={
            <>
              <Button kind="ghost" disabled={deleteBusy} onPress={() => setConfirmDelete(false)}>
                Keep it
              </Button>
              <Button kind="danger" busy={deleteBusy} onPress={remove}>
                Delete event
              </Button>
            </>
          }
        >
          <p className="text-sm m-0">
            {`“${event.title}” will be removed. `}
            {toldCount === 0
              ? 'Nobody else is invited, so nobody is told.'
              : `${toldCount} ${toldCount === 1 ? 'person is' : 'people are'} told it is cancelled.`}
          </p>
        </Modal>
      )}
    </Panes.Column>
  );
}
